import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "use-analytics";

import { useLearnerAuth } from "../../providers/LearnerAuthProvider";
import {
  QUIZ_GENERATION_PROGRESS_CAP,
  QUIZ_TOPIC_GENERATOR_WS_PATH,
} from "./consts";

export type QuizWriterUnlockedTopic = {
  uid: string;
  name: string;
  source: "quiz" | "prior_topics";
};

interface UseQuizTopicGeneratorWebsocketOptions {
  refetchTopicSuggestions: () => unknown;
  refreshDidYouKnowFact: () => void;
}

export function useQuizTopicGeneratorWebsocket({
  refetchTopicSuggestions,
  refreshDidYouKnowFact,
}: UseQuizTopicGeneratorWebsocketOptions) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const { sessionTokenStore } = useLearnerAuth();

  const [isTopicsGenerating, setIsTopicsGenerating] = useState(false);
  const [topicsGenerationLogStepCount, setTopicsGenerationLogStepCount] =
    useState(0);
  const [latestTopicGenUserLogMessage, setLatestTopicGenUserLogMessage] =
    useState<string | null>(null);
  const [topicsGenError, setTopicsGenError] = useState<string | null>(null);
  const [unlockedTopics, setUnlockedTopics] = useState<
    QuizWriterUnlockedTopic[] | null
  >(null);

  const topicGenSocketRef = useRef<WebSocket | null>(null);
  const topicsGenerationGotResultRef = useRef(false);

  const closeTopicGeneratorWebsocket = useCallback(() => {
    topicGenSocketRef.current?.close();
    topicGenSocketRef.current = null;
  }, []);

  const resetTopicGeneration = useCallback(() => {
    closeTopicGeneratorWebsocket();
    setIsTopicsGenerating(false);
    setUnlockedTopics(null);
    setTopicsGenError(null);
  }, [closeTopicGeneratorWebsocket]);

  const startTopicGeneratorWebsocket = useCallback(
    (completedAttemptUid: string) => {
      const token = sessionTokenStore?.getSessionToken();
      if (!token) {
        setTopicsGenError(t("Please sign in to generate a quiz."));
        return;
      }

      topicGenSocketRef.current?.close();
      setTopicsGenError(null);
      setUnlockedTopics(null);
      setTopicsGenerationLogStepCount(0);
      setLatestTopicGenUserLogMessage(null);
      topicsGenerationGotResultRef.current = false;
      setIsTopicsGenerating(true);
      refreshDidYouKnowFact();

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(
        `${protocol}//${window.location.host}${QUIZ_TOPIC_GENERATOR_WS_PATH}`,
      );
      topicGenSocketRef.current = ws;

      let payloadSent = false;
      const sendPayload = () => {
        if (payloadSent || ws.readyState !== WebSocket.OPEN) {
          return;
        }
        payloadSent = true;
        ws.send(JSON.stringify({ quizAttemptUid: completedAttemptUid }));
      };

      ws.onopen = () => {
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data as string) as {
            type?: string;
            message?: string;
            topics?: Array<{
              uid: string;
              name: string;
              source?: "quiz" | "prior_topics";
            }>;
          };
          switch (data.type) {
            case "ready":
              sendPayload();
              break;
            case "user_log":
              if (typeof data.message === "string" && data.message.length > 0) {
                setLatestTopicGenUserLogMessage(data.message);
                setTopicsGenerationLogStepCount(prev =>
                  Math.min(prev + 1, QUIZ_GENERATION_PROGRESS_CAP),
                );
              }
              break;
            case "result": {
              topicsGenerationGotResultRef.current = true;
              if (Array.isArray(data.topics) && data.topics.length > 0) {
                const normalized = data.topics.map(topic => ({
                  uid: topic.uid,
                  name: topic.name,
                  source: topic.source as "quiz" | "prior_topics",
                }));
                setUnlockedTopics(normalized);
                void refetchTopicSuggestions();
                track("quiz_topics_unlocked", {
                  topicCount: normalized.length,
                  fromQuizCount: normalized.filter(
                    topic => topic.source === "quiz",
                  ).length,
                  fromPriorTopicsCount: normalized.filter(
                    topic => topic.source === "prior_topics",
                  ).length,
                });
              } else {
                setTopicsGenError(t("Something went wrong."));
              }
              setIsTopicsGenerating(false);
              topicGenSocketRef.current = null;
              break;
            }
            case "error":
              setTopicsGenError(data.message ?? t("Something went wrong."));
              setIsTopicsGenerating(false);
              topicGenSocketRef.current = null;
              break;
            default:
              break;
          }
        } catch (err) {
          OsPlatform.captureError(err, {
            component: Component.QuizWriter,
          });
        }
      };

      ws.onclose = () => {
        if (topicGenSocketRef.current !== ws) {
          return;
        }
        topicGenSocketRef.current = null;
        setIsTopicsGenerating(prev => {
          if (prev && !topicsGenerationGotResultRef.current) {
            setTopicsGenError(t("Connection closed before topics finished."));
          }
          return false;
        });
      };

      ws.onerror = () => {
        setTopicsGenError(t("WebSocket connection error."));
        setIsTopicsGenerating(false);
        topicGenSocketRef.current = null;
      };
    },
    [
      refetchTopicSuggestions,
      refreshDidYouKnowFact,
      sessionTokenStore,
      t,
      track,
    ],
  );

  useEffect(() => {
    return () => {
      closeTopicGeneratorWebsocket();
    };
  }, [closeTopicGeneratorWebsocket]);

  return {
    isTopicsGenerating,
    setIsTopicsGenerating,
    topicsGenerationLogStepCount,
    latestTopicGenUserLogMessage,
    topicsGenError,
    unlockedTopics,
    resetTopicGeneration,
    startTopicGeneratorWebsocket,
    closeTopicGeneratorWebsocket,
  };
}
